import { ActivitySnapshotSchema, EVENTS_PATH, StudioEventSchema, type ActivitySnapshot, type StudioEvent } from '@studio/shared';
import { api } from '../api.js';
import { createActivityReconciler, type ActivityState } from './reconcile.js';

export interface ActivityTransport {
  /** Opens the stream and returns how to close it. Called before the snapshot is read. */
  open(onEvent: (event: StudioEvent) => void): () => void;
  readSnapshot(): Promise<ActivitySnapshot>;
}

export interface ActivityClient {
  state(): ActivityState;
  close(): void;
}

/**
 * How long a burst of unnamed Jobs is allowed to gather before one snapshot names them
 * all. Six Slots start together; six snapshot reads for one press would be the cost of
 * the detail, not the detail.
 */
const REFILL_DELAY_MS = 300;

/**
 * How long to wait before asking for the snapshot again when the first ask failed.
 *
 * The snapshot is one authenticated GET made as the shell starts, and there is exactly
 * one thing worse than it failing: it failing quietly. Without a retry the count is 0
 * and stays 0 for the rest of the session, the indicator never appears however much
 * work runs, and nothing anywhere goes red - which is how this was shipped twice. So a
 * refusal is a reason to ask again, backing off to a ceiling rather than knocking.
 */
const SNAPSHOT_RETRY_MIN_MS = 500;
const SNAPSHOT_RETRY_MAX_MS = 10_000;

/**
 * Open the stream, buffer, then read the snapshot, then apply only what is newer.
 * Doing it in the other order loses every change that lands while the snapshot is in
 * flight, which is exactly the window a reconnect happens in.
 *
 * The stream carries a resource, an id, a version and a state - and nothing else. A Job
 * that starts after the snapshot was read is therefore known to be running and known by
 * nothing else: no kind, no App, no Bulk Run. That is enough for a count and not enough
 * for the detail R177 asks for, so a name the client cannot describe is what asks for
 * another snapshot.
 */
export function createActivityClient(transport: ActivityTransport, onChange: (state: ActivityState) => void): ActivityClient {
  const reconciler = createActivityReconciler();
  let closed = false;
  let reading = false;
  let pending: ReturnType<typeof setTimeout> | null = null;
  let retrying: ReturnType<typeof setTimeout> | null = null;
  let backoffMs = 0;
  /** Ids a snapshot has already been asked for. One read per Job, however it ends. */
  const asked = new Set<string>();

  /**
   * Ask again, but only while there has never been a snapshot.
   *
   * After one has been applied the stream carries every change, so a failed refill
   * costs detail on one line of the panel and never the count itself. Before one, the
   * client is claiming there is no work when it simply does not know.
   */
  const askAgain = (): void => {
    if (closed || retrying !== null || reconciler.state().snapshotApplied) return;
    backoffMs = backoffMs === 0 ? SNAPSHOT_RETRY_MIN_MS : Math.min(backoffMs * 2, SNAPSHOT_RETRY_MAX_MS);
    retrying = setTimeout(() => {
      retrying = null;
      read();
    }, backoffMs);
  };

  const read = (): void => {
    if (closed || reading) return;
    reading = true;
    void transport
      .readSnapshot()
      .then((snapshot) => {
        if (closed) return;
        backoffMs = 0;
        reconciler.applySnapshot(snapshot);
        onChange(reconciler.state());
      })
      .catch(() => {
        // Refused, unreachable, or answering something this client cannot parse. Which
        // of those it was changes nothing here: the count is unknown, not zero.
        askAgain();
      })
      .finally(() => {
        reading = false;
      });
  };

  const nameTheUnnamed = (): void => {
    if (pending !== null) return;
    const missing = reconciler.state().unknown.filter((id) => !asked.has(id));
    if (missing.length === 0) return;
    for (const id of missing) asked.add(id);
    pending = setTimeout(() => {
      pending = null;
      read();
    }, REFILL_DELAY_MS);
  };

  const close = transport.open((event) => {
    if (closed) return;
    if (!reconciler.receive(event)) return;
    onChange(reconciler.state());
    nameTheUnnamed();
  });

  read();

  return {
    state: () => reconciler.state(),
    close: () => {
      closed = true;
      if (pending !== null) clearTimeout(pending);
      if (retrying !== null) clearTimeout(retrying);
      pending = null;
      retrying = null;
      close();
    },
  };
}

/**
 * The real transport: the browser's own event stream and one authenticated GET.
 *
 * The GET goes through `api`, not through a `fetch` written here. Written here it was
 * outside the one client, so the 401 that ends a session reached nobody: the read
 * failed, the retry above swallowed it, and the shell - which promises that any 401
 * closes it - never heard a thing. A background read is exactly the read that has to
 * be able to say it was refused, because there is no operator watching it.
 */
export function browserTransport(): ActivityTransport {
  return {
    open(onEvent) {
      const source = new EventSource(EVENTS_PATH, { withCredentials: true });
      const handler = (message: MessageEvent<string>): void => {
        const parsed = StudioEventSchema.safeParse(JSON.parse(message.data));
        if (parsed.success) onEvent(parsed.data);
      };
      source.addEventListener('resource', handler as EventListener);
      return () => source.close();
    },
    async readSnapshot() {
      return ActivitySnapshotSchema.parse(await api.readActivity());
    },
  };
}
